import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, TextField, TablePagination, Button } from '@mui/material';

const App = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [changedRows, setChangedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const auth = urlParams.get('auth');

  useEffect(() => {
    const fetchData = async () => {
      if (!auth) {
        console.log("Auth parameter is missing!");
        return;
      }
      
      try {
        const response = await fetch(`https://guanyin.geovisiongroup.com/service/rest/lalorraine/product?auth=${auth}`);
        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching data: ", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [auth]);

  const handleInputChange = (e, id, column) => {
    const value = e.target.value;

    const updatedData = data.map(row => 
      row.univeraUrunKod === id ? { ...row, [column]: value } : row
    );
    setData(updatedData);

    const updatedRow = updatedData.find(row => row.univeraUrunKod === id);

    setChangedRows(prevChangedRows => {
      const existingIndex = prevChangedRows.findIndex(row => row.univeraUrunKod === id);
      if (existingIndex > -1) {
        const updatedChangedRows = [...prevChangedRows];
        updatedChangedRows[existingIndex] = updatedRow;
        return updatedChangedRows;
      } else {
        return [...prevChangedRows, updatedRow];
      }
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUpdate = async () => {
    if (changedRows.length > 0) {
      if (!auth) {
        console.log("Auth parameter is missing!");
        return;
      }

      try {
        const response = await fetch(`https://guanyin.geovisiongroup.com/service/rest/lalorraine/product?auth=${auth}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(changedRows),
        });

        if (response.ok) {
          alert('Güncellemeler başarıyla gönderildi!');
          setChangedRows([]);
        } else {
          alert('Güncelleme başarısız oldu!');
        }
      } catch (error) {
        console.error("Error sending data: ", error);
        alert('Güncelleme sırasında bir hata oluştu!');
      }
    } else {
      alert('Değişiklik yapılmadı!');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const normalizeString = (str) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  };
  
  const filteredData = data.filter(row =>
    (normalizeString(row.univeraUrunKod || '').includes(normalizeString(searchTerm))) ||
    (normalizeString(row.ad || '').includes(normalizeString(searchTerm))) ||
    (normalizeString(row.kisaAd || '').includes(normalizeString(searchTerm))) ||
    (normalizeString(row.textUrunKod || '').includes(normalizeString(searchTerm))) ||
    (normalizeString(row.aliciUrunkod || '').includes(normalizeString(searchTerm))) || 
    (normalizeString(row.getirUrunkod || '').includes(normalizeString(searchTerm))) ||
    (normalizeString(row.getirBarcode || '').includes(normalizeString(searchTerm))) ||
    (normalizeString(row.yemekSepetiUrunkod || '').includes(normalizeString(searchTerm)))
  );

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom align="center" sx={{ color: '#1976d2' }}>
        Ürün Tablosu
      </Typography>

      {/* Update Button and Search Input */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <TextField
          label="Ara"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: 300 }}
        />
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleUpdate}
        >
          Güncelle
        </Button>
      </Box>

      {/* TableContainer */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="editable table">
          {/* Table Headers */}
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Univera Ürün Kodu</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Text Ürun Kodu</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Ad</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Kısa Ad</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Migros Ürün Kodu</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Getir Ürün Kodu</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>Getir Barcode</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>YemekSepeti Ürün Kodu</TableCell>
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Loading...</TableCell>
              </TableRow>
            ) : (
              filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow key={row.univeraUrunKod} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                  <TableCell align="center">{row.univeraUrunKod}</TableCell>
                  <TableCell align="center">{row.textUrunKod}</TableCell>
                  <TableCell align="center">{row.ad}</TableCell>
                  <TableCell align="center">{row.kisaAd}</TableCell>
                  {/* Editable columns */}
                  <TableCell align="center">
                    <TextField
                      value={row.aliciUrunkod}
                      onChange={(e) => handleInputChange(e, row.univeraUrunKod, 'aliciUrunkod')}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      value={row.getirUrunkod}
                      onChange={(e) => handleInputChange(e, row.univeraUrunKod, 'getirUrunkod')}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{row.getirBarcode}</TableCell>
                  <TableCell align="center">
                    <TextField
                      value={row.yemekSepetiUrunkod}
                      onChange={(e) => handleInputChange(e, row.univeraUrunKod, 'yemekSepetiUrunkod')}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default App;
